import React, { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { CustomBottomSheetModalProvider } from '@/feature/alerts/components/CustomBottomSheetModalProvider';
import {
    LineupUpdateModal,
    LineupUpdateModalRef,
    LineupUpdateProps,
} from '@/feature/betslip-pickem/components/LineupUpdateModal';

import { PicksPlayerPropsStoreUpdater } from '../hooks/use-player-props-store';

export type LineupUpdateContextType = {
    showLineupUpdateModal: (props: LineupUpdateProps) => void;
};

const LineupUpdateContext = React.createContext<LineupUpdateContextType | undefined>(undefined);

/**
 * Provides access to the Modal used to display the list of unavailable players + players with projection changes from betslip
 */
export const LineupUpdateProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const modalRef = useRef<LineupUpdateModalRef>(null);

    const showLineupUpdateModal = useCallback((props: LineupUpdateProps) => {
        if (
            [
                props?.invalidPicksByEvent,
                props.picksWithChangedProjections,
                props.picksWithMissingEvents,
                props.playersRemoved,
            ].some(it => (it?.length ?? 0) > 0)
        ) {
            modalRef.current?.show(props);
        }
    }, []);

    const value: LineupUpdateContextType = useMemo(
        () => ({
            showLineupUpdateModal,
        }),
        [showLineupUpdateModal]
    );

    useEffect(() => {
        PicksPlayerPropsStoreUpdater.setModalContext(value);
        return () => {
            PicksPlayerPropsStoreUpdater.setModalContext(null);
        };
    }, [value]);

    return (
        <CustomBottomSheetModalProvider>
            <LineupUpdateContext.Provider value={value}>
                {children}
                <LineupUpdateModal ref={modalRef} />
            </LineupUpdateContext.Provider>
        </CustomBottomSheetModalProvider>
    );
};

export const useLineupUpdate = () => {
    const context = useContext(LineupUpdateContext);
    if (context === undefined) {
        throw new Error('useLineupUpdate must be used within a LineupUpdateProvider');
    }
    return context;
};
